import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { CAPABILITIES, CAP_IDS } from "@/content/software";
import { ExplorerFrame, Swap } from "@/components/site/ExplorerFrame";
import { Mono, TextLink } from "@/components/site/primitives";
import { cn } from "@/lib/utils";
import { CapabilitySignature } from "./CapabilitySignature";

const FAMILY_LABEL = { ordo: "Ordo software family", independent: "Independent offering" };

const Selector = ({ sel, onSelect }) => (
  <div className="grid grid-cols-2 gap-3 border-b border-o-line p-4 sm:p-5 md:grid-cols-4">
    <div className="col-span-2 hidden border-x border-t border-o-strong px-3 py-2 md:block"><Mono accent>Ordo software family</Mono></div>
    <div className="hidden border-x border-t border-dashed border-o-strong px-3 py-2 md:block"><Mono>Independent offering</Mono></div>
    <div className="hidden border-x border-t border-dashed border-o-strong px-3 py-2 md:block"><Mono>Independent offering</Mono></div>
    {CAP_IDS.map((id) => {
      const c = CAPABILITIES[id];
      const on = sel === id;
      return (
        <button key={id} type="button" onClick={() => onSelect(id)} aria-pressed={on} data-testid={`software-cap-${id}`}
          className={cn("flex flex-col gap-4 border p-4 text-left transition-colors", on ? "border-o-accent bg-o-soft" : "border-o-line bg-o-bg hover:border-o-strong")}>
          <span aria-hidden className="h-1 w-full transition-opacity" style={{ background: c.hue, opacity: on ? 1 : 0.4 }} />
          <span className="flex items-center justify-between gap-2">
            <Mono>{c.index}</Mono>
            <Mono className="md:hidden">{c.family === "ordo" ? "Ordo family" : "Independent"}</Mono>
          </span>
          <span>
            <span className="block font-display text-lg font-bold leading-tight">{c.name}</span>
            <span className="mt-1 block text-sm text-o-dim">{c.responsibility}</span>
          </span>
        </button>
      );
    })}
  </div>
);

const Detail = ({ cap, onSelect }) => (
  <div className="grid lg:grid-cols-12" data-testid="software-detail">
    <div className="border-b border-o-line p-5 sm:p-8 lg:col-span-5 lg:border-b-0 lg:border-r">
      <span className="font-mono text-[10.5px] uppercase tracking-[0.2em]" style={{ color: cap.hue }}>{FAMILY_LABEL[cap.family]}</span>
      <h3 data-testid="software-detail-title" className="mt-3 font-display text-3xl font-bold">{cap.name}</h3>
      <p className="mt-1 font-display text-lg text-o-dim">{cap.responsibility}</p>
      <p className="mt-5 leading-relaxed">{cap.statement}</p>
      <div className="mt-6 border-l-2 pl-4" style={{ borderColor: cap.hue }}>
        <Mono>Operates</Mono>
        <p className="mt-1 text-sm">{cap.alone}</p>
      </div>
      <div className="mt-6">
        <Mono>Can also work with</Mono>
        <ul className="mt-2 space-y-2">
          {Object.entries(cap.pairs).map(([id, note]) => (
            <li key={id}>
              <button type="button" onClick={() => onSelect(id)} data-testid={`software-pair-${id}`} className="text-left text-sm transition-colors hover:text-o-accent">
                <span className="font-semibold">{CAPABILITIES[id].name}</span> <span className="text-o-dim">· {note}</span>
              </button>
            </li>
          ))}
        </ul>
      </div>
      <TextLink to={cap.path} state={{ via: "software" }} testid="software-detail-examine" className="mt-7">Examine {cap.name}</TextLink>
    </div>
    <div className="p-5 sm:p-8 lg:col-span-7">
      <CapabilitySignature cap={cap} />
    </div>
  </div>
);

const ROWS = [
  ["Responsibility", (c) => c.responsibility],
  ["What it concerns", (c) => c.concerns],
  ["Belongs to", (c) => FAMILY_LABEL[c.family]],
  ["Operates", (c) => c.alone],
  ["Relationship to General Ordo", (c) => c.ordoRelation],
];

const CompareTable = () => (
  <div className="overflow-x-auto">
    <table className="w-full min-w-[820px] text-left text-sm" data-testid="software-compare-table">
      <thead>
        <tr>
          <th className="w-44 p-5 align-bottom"><Mono>Capability</Mono></th>
          {CAP_IDS.map((id) => (
            <th key={id} className="p-5 align-bottom">
              <span aria-hidden className="block h-1 w-10" style={{ background: CAPABILITIES[id].hue }} />
              <span className="mt-3 block font-display text-base font-bold">{CAPABILITIES[id].name}</span>
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {ROWS.map(([label, fn]) => (
          <tr key={label} className="border-t border-o-line">
            <th scope="row" className="p-5 align-top font-normal"><Mono>{label}</Mono></th>
            {CAP_IDS.map((id) => <td key={id} className="p-5 align-top text-o-dim">{fn(CAPABILITIES[id])}</td>)}
          </tr>
        ))}
        <tr className="border-t border-o-line">
          <th scope="row" className="p-5 font-normal"><Mono>Examine</Mono></th>
          {CAP_IDS.map((id) => (
            <td key={id} className="p-5">
              <TextLink to={CAPABILITIES[id].path} state={{ via: "software" }} testid={`compare-link-${id}`}>{CAPABILITIES[id].name}</TextLink>
            </td>
          ))}
        </tr>
      </tbody>
    </table>
  </div>
);

export const SoftwareExplorer = () => {
  const [params, setParams] = useSearchParams();
  const sel = CAP_IDS.includes(params.get("capability")) ? params.get("capability") : "engine";
  const [mode, setMode] = useState("explore");
  const setSel = (id) => {
    const next = new URLSearchParams(params);
    next.set("capability", id);
    setParams(next, { replace: true });
  };
  return (
    <ExplorerFrame
      testid="software-explorer"
      label="Software relationship explorer"
      hint="Select a capability. Each can be considered on its own; none requires the others."
      mode={mode}
      onMode={setMode}
      modes={[["explore", "Explore"], ["text", "Compare all four"]]}
    >
      {mode === "explore" ? (
        <>
          <Selector sel={sel} onSelect={setSel} />
          <div aria-live="polite">
            <Swap k={sel}>
              <Detail cap={CAPABILITIES[sel]} onSelect={setSel} />
            </Swap>
          </div>
        </>
      ) : (
        <CompareTable />
      )}
    </ExplorerFrame>
  );
};
